import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

type StrategyTableColumn = CustomEmberTableColumn & {
  id: number;
  isEditing: boolean;
  isSaveStrategyTaskRunning: boolean;
  onDayOfWeekValueChange: (
    dow: number,
    roomId: string,
    value: number | null,
    type: 'minimum' | 'maximum',
  ) => void;
  strategy: RecommendationStrategy;
};

type StrategyTableRow = CustomEmberTableRow & {
  roomId: string;
  roomName: string;
};

interface SettingsRecommendationsPriceRangeTdMultiRoomDowSignature {
  Args: {
    column: StrategyTableColumn;
    row: StrategyTableRow;
    value: {
      maximum: number | null;
      minimum: number | null;
    };
  };
}

export default class TdMultiRoomDow extends Component<SettingsRecommendationsPriceRangeTdMultiRoomDowSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  debouncedOnDayOfWeekValueChangeTask = task(
    { restartable: true },
    async (
      dowValue: number | null,
      type: 'minimum' | 'maximum',
    ): Promise<void> => {
      await timeout('extra-long');
      this.setDayOfWeekValue(dowValue, type);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    const minimum =
      this.args.value.minimum !== null
        ? this.hotelAndMarketService.formatRate(this.args.value.minimum)
        : this.intl.t('pricing_assistant.recommendations.price_range.no_min');

    const maximum =
      this.args.value.maximum !== null
        ? this.hotelAndMarketService.formatRate(this.args.value.maximum)
        : this.intl.t('pricing_assistant.recommendations.price_range.no_max');

    return {
      minimum,
      maximum,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputsHaveErrors() {
    // Check if input field belongs to column containing day of week with errors and row containing room with errors
    const { strategy, id: columnId } = this.args.column;

    return strategy.multiRoomPriceRangeStrategyDayOfWeekWithErrors.some(
      (day) => day.dow === columnId && day.roomId === this.args.row.roomId,
    );
  }

  setDayOfWeekValue(
    dowValue: number | null,
    type: 'minimum' | 'maximum',
  ): void {
    const { column, row } = this.args;

    this.args.column.onDayOfWeekValueChange(
      column.id,
      row.roomId,
      dowValue,
      type,
    );
  }

  @action onDayOfWeekMaximumFocusOut(event: Event): void {
    void this.debouncedOnDayOfWeekValueChangeTask.cancelAll();
    const target = event.target as HTMLInputElement;
    const value = target?.value ? parseInt(target.value, 10) : null;
    this.setDayOfWeekValue(value, 'maximum');
  }

  @action onDayOfWeekMaximumValueChange(dowValue: number | null): void {
    void this.debouncedOnDayOfWeekValueChangeTask.perform(dowValue, 'maximum');
  }

  @action onDayOfWeekMinimumFocusOut(event: Event): void {
    void this.debouncedOnDayOfWeekValueChangeTask.cancelAll();
    const target = event.target as HTMLInputElement;
    const value = target?.value ? parseInt(target.value, 10) : null;
    this.setDayOfWeekValue(value, 'minimum');
  }

  @action onDayOfWeekMinimumValueChange(dowValue: number | null): void {
    void this.debouncedOnDayOfWeekValueChangeTask.perform(dowValue, 'minimum');
  }

  <template>
    <div
      class="flex flex-col py-100 {{if @column.isEditing 'gap-200' 'gap-400'}}"
    >
      {{#if @column.isEditing}}
        <PrismNumberInput
          @disabled={{@column.isSaveStrategyTaskRunning}}
          @hasErrors={{this.numberInputsHaveErrors}}
          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
          @isSmall={{true}}
          @isTouched={{true}}
          @min={{0}}
          @onFocusOut={{this.onDayOfWeekMinimumFocusOut}}
          @onValueChange={{this.onDayOfWeekMinimumValueChange}}
          @placeholder={{t "general.minimum.short"}}
          @removePadding={{true}}
          @value={{@value.minimum}}
        />
        <PrismNumberInput
          @disabled={{@column.isSaveStrategyTaskRunning}}
          @hasErrors={{this.numberInputsHaveErrors}}
          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
          @isSmall={{true}}
          @isTouched={{true}}
          @min={{0}}
          @onFocusOut={{this.onDayOfWeekMaximumFocusOut}}
          @onValueChange={{this.onDayOfWeekMaximumValueChange}}
          @placeholder={{t "general.maximum.short"}}
          @removePadding={{true}}
          @value={{@value.maximum}}
        />
      {{else}}
        <span>{{this.formattedValue.minimum}}</span>
        <span>{{this.formattedValue.maximum}}</span>
      {{/if}}
    </div>
  </template>
}
